import React, { useEffect, useState } from "react"
import { Box, Button } from "src/UILibrary"
import { useTranslation } from "react-i18next"
import InputTextField from "src/components/atom/InputTextField"
import { validateField } from "src/modules/validation"
import * as yup from "yup"
import { OutlinedButton } from "src/components/buttons/OutlinedButton"
import FileSelectField from "src/components/atom/FileSelectField"
import { Company, Product, ProductService } from "src/openapi"
import { useNavigate } from "react-router-dom"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"
import { useLoginContext } from "src/hooks/useLoginContext"
import { RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { ShowProgress } from "src/states/showProgress"

type Props = {
  item: Company
}

export const ServiceDetail = ({ item }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.user" })

  const { accessToken, checkLoginStatus } = useLoginContext()

  const navigate = useNavigate()

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [file, setFile] = useState<string>()
  const [title, setTitle] = useState<string>("")
  const [redirectURL, setRedirectURL] = useState<string>("")
  const [description, setDescription] = useState<string>("")

  const [fileValidationMessage, setFileValidationMessage] = useState<string>("")
  const [titleValidiationMessage, setTitleValidationMessage] = useState<string>("")
  const [redirectURLValidationMessage, setRedirectURLValidationMessage] = useState<string>("")
  const [descriptionValidationMessage, setDescriptionValidationMessage] = useState<string>("")

  const [companyService, setCompanyService] = useState<Product>()

  useEffect(() => {
    ProductService.productDetail(item.id as number)
      .then((res) => {
        if (res.status === RESPONSE_CODE.SUCCESS) {
          setCompanyService(res.data)
          setFile(res.data?.imageUrl)
          setTitle(res.data?.title as string)
          setRedirectURL(res.data?.linkUrl as string)
          setDescription(res.data?.description as string)
        } else if (res.status === RESPONSE_CODE.NOT_FOUND) {
          // setShowToast({ show: true, text: t("no_job_info"), type: SNACKBAR_TYPES.INFORMATION })
        } else {
          setShowToast({
            show: true,
            text: t("failed_get_company_service"),
            type: SNACKBAR_TYPES.ERROR,
          })
        }
      })
      .catch(() => {
        setShowToast({
          show: true,
          text: t("failed_get_company_service"),
          type: SNACKBAR_TYPES.ERROR,
        })
      })
  }, [])

  const validate = async () => {
    let validation = true

    if (file) {
      setFileValidationMessage("")
    } else {
      setFileValidationMessage(t("please_select_file"))
    }

    try {
      await validateField(
        yup.object().shape({
          title: yup.string().required(t("please_input_company_name")),
        }),
        "title",
        title
      )
      setTitleValidationMessage("")
    } catch (err: any) {
      setTitleValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          redirectURL: yup
            .string()
            .required(t("please_input_redirect_url"))
            .matches(
              /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/,
              t("please_input_correct_site_url")
            ),
        }),
        "redirectURL",
        redirectURL
      )
      setRedirectURLValidationMessage("")
    } catch (err: any) {
      setRedirectURLValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          description: yup.string().required(t("please_input_company_name")),
        }),
        "description",
        description
      )
      setDescriptionValidationMessage("")
    } catch (err: any) {
      setDescriptionValidationMessage(err.message)
      validation = validation && false
    }

    return validation
  }

  const handleUpdate = async () => {
    if (!(await validate())) {
      return
    }

    if (companyService) {
      setShowProgress(true)
      ProductService.updateProduct(accessToken, companyService.id as number, {
        title: title,
        imageUrl: file,
        description: description,
        linkUrl: redirectURL,
      })
        .then((res) => {
          setShowProgress(false)
          if (!checkLoginStatus(res)) {
            return
          }

          if (res.status === RESPONSE_CODE.SUCCESS) {
            setShowToast({
              show: true,
              text: t("success_update_company_service"),
              type: SNACKBAR_TYPES.SUCCESS,
            })
            navigate("/mypage/users/list/company")
          } else {
            setShowToast({
              show: true,
              text: t("failed_update_company_service"),
              type: SNACKBAR_TYPES.ERROR,
            })
          }
        })
        .catch(() => {
          setShowProgress(false)
          setShowToast({
            show: true,
            text: t("failed_update_company_service"),
            type: SNACKBAR_TYPES.ERROR,
          })
        })
    } else {
      setShowProgress(true)
      ProductService.createProduct(accessToken, {
        companyId: item.id as number,
        title: title,
        imageUrl: file as string,
        description: description,
        linkUrl: redirectURL,
      })
        .then((res) => {
          setShowProgress(false)
          if (!checkLoginStatus(res)) {
            return
          }

          if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
            setShowToast({
              show: true,
              text: t("success_update_company_service"),
              type: SNACKBAR_TYPES.SUCCESS,
            })
            navigate("/mypage/users/list/company")
          } else {
            setShowToast({
              show: true,
              text: t("failed_update_company_service"),
              type: SNACKBAR_TYPES.ERROR,
            })
          }
        })
        .catch(() => {
          setShowProgress(false)
          setShowToast({
            show: true,
            text: t("failed_update_company_service"),
            type: SNACKBAR_TYPES.ERROR,
          })
        })
    }
  }

  const handleRemove = () => {
    if (companyService) {
      setShowProgress(true)
      ProductService.deleteProduct(accessToken, companyService.id as number)
        .then((res) => {
          setShowProgress(false)
          if (!checkLoginStatus(res)) {
            return
          }

          if (res.status === RESPONSE_CODE.SUCCESS) {
            setShowToast({
              show: true,
              text: t("success_remove_company_service"),
              type: SNACKBAR_TYPES.SUCCESS,
            })
          } else {
            setShowToast({
              show: true,
              text: t("failed_remove_company_service"),
              type: SNACKBAR_TYPES.ERROR,
            })
          }
        })
        .catch(() => {
          setShowProgress(false)
          setShowToast({
            show: true,
            text: t("failed_remove_company_service"),
            type: SNACKBAR_TYPES.ERROR,
          })
        })
    } else {
      setShowToast({ show: true, text: t("no_service_info"), type: SNACKBAR_TYPES.WARNING })
    }
  }

  return (
    <Box sx={{ py: "40px", px: "20px" }}>
      <FileSelectField
        file={file}
        handleChange={setFile}
        name={t("banner")}
        required
        validationMessage={fileValidationMessage}
        labelStyle={{ fontWeight: "400", fontFamily: "Noto Sans JP !important", fontSize: "16px" }}
      />
      <InputTextField
        name={t("service_title")}
        text={title}
        handleText={setTitle}
        required
        validationMessage={titleValidiationMessage}
        sx={{ mt: "30px" }}
      />
      <InputTextField
        name={t("redirect_url")}
        text={redirectURL}
        handleText={setRedirectURL}
        required
        validationMessage={redirectURLValidationMessage}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
      />
      <InputTextField
        name={t("description")}
        text={description}
        handleText={setDescription}
        required
        validationMessage={descriptionValidationMessage}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
      />

      <Box display="flex" sx={{ mt: "30px" }}>
        <OutlinedButton
          caption={t("delete")}
          fontColor="#FC3317"
          fontWeight="700"
          fontSize={16}
          sx={{
            borderColor: "#FC3317",
            minWidth: "240px",
            py: "16px",
            fontSize: "1rem",
            fontFamily: "Noto Sans JP !important",
            lineHeight: "1rem",
            fontWeight: 700,
            ml: "auto",
            mr: "40px",
            display: "flex",
            justifyContent: "center",
            borderRadius: 0,
          }}
          onClick={() => handleRemove()}
        />
        <Button
          variant="contained"
          sx={{
            minWidth: "240px",
            py: "16px",
            fontSize: "1rem",
            fontFamily: "Noto Sans JP !important",
            lineHeight: "1rem",
            fontWeight: 700,
            ml: 0,
            mr: "auto",
            display: "flex",
            justifyContent: "center",
            borderRadius: 0,
            ":hover": {
              bgcolor: "#2DA389",
            },
          }}
          onClick={() => handleUpdate()}
        >
          {t("update")}
        </Button>
      </Box>
    </Box>
  )
}
